'use client';

import { cn } from '@repo/utils/cn';
import { useEffect, useState } from 'react';

type CountDownProgressProps = {
  size?: number;
  time?: number;
  trackWidth?: number;
  indicatorWidth?: number;
  className?: string;
  trackColor?: string;
  indicatorColor?: string;
  withProgressIndicator?: boolean;
  onComplete?: () => void;
};

const getWrapperStyle = (size: number): React.CSSProperties => ({
  position: 'relative',
  width: `${size}px`,
  height: `${size}px`,
});

const CountDownTimer = ({
  size = 80,
  time = 60000,
  trackWidth = 4,
  indicatorWidth = 4,
  className,
  withProgressIndicator = false,
  trackColor = 'var(--atoms-tag-primary-bg)',
  indicatorColor = 'var(--colors-accents-accent-2-600)',
  onComplete,
}: CountDownProgressProps) => {
  const [progress, setProgress] = useState<number>(time);

  useEffect(() => {
    const timer = () => {
      setProgress((prevProgress) => {
        if (prevProgress <= 1000) {
          onComplete && onComplete();
          return 0;
        }
        return prevProgress - 1000;
      });
    };

    if (progress > 0) {
      const timeout = setTimeout(timer, 1000);
      return () => clearTimeout(timeout);
    }
  }, [progress]);

  const center = size / 2;
  const radius = center - Math.max(trackWidth, indicatorWidth);
  const dashArray = 2 * Math.PI * radius;
  const dashOffset = dashArray * ((time + progress) / time);

  const formatTimeInSeconds = (ms: number) => {
    const seconds = ((ms % 60000) / 1000).toFixed(0);

    return seconds;
  };

  if (withProgressIndicator) {
    return (
      <div
        data-testid="countdown-timer-with-progressbar"
        style={getWrapperStyle(size)}
      >
        <svg
          className={cn('-rotate-[85deg]', className)}
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
        >
          <circle
            stroke={trackColor}
            cx={center}
            fill={'transparent'}
            cy={center}
            r={radius}
            strokeWidth={trackWidth}
          />
          <circle
            className="transition-[stroke-dashoffset] duration-1000 ease-linear"
            stroke={indicatorColor}
            cx={center}
            cy={center}
            r={radius}
            strokeWidth={indicatorWidth}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            fill={'transparent'}
          />
        </svg>
      </div>
    );
  }

  return <span data-testid="countdown-timer-in-seconds">{formatTimeInSeconds(progress)}</span>;
};

export { CountDownTimer };
